import { Button, Callout, Field, buttonClass } from "@gf/ui";
import { CircleCheck, LinkIcon } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router";
import { MIN_PASSWORD } from "../../components/accounts/shared";
import { ApiError, authApi } from "../../lib/api";
import { AuthHeading, AuthLayout } from "./AuthLayout";
import { PasswordInput } from "./PasswordInput";

/**
 * /set-password?token=…&mode=invite|reset — invite links ("Set your password")
 * and password resets ("Reset your password"). Both POST /api/auth/reset.
 */
export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const isInvite = params.get("mode") === "invite" || params.has("invite");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [touched, setTouched] = useState(false);

  const mismatch = confirm.length > 0 && confirm !== password;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (password.length < MIN_PASSWORD || password !== confirm) return;
    setBusy(true);
    setError(null);
    try {
      await authApi.reset(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && (err.code === "invalid_token" || err.status === 404 || err.status === 410)) setExpired(true);
      else setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const pageTitle = isInvite ? "Set your password" : "Reset your password";

  if (!token || expired) {
    return (
      <AuthLayout title={pageTitle}>
        <div className="gfp-auth__icon gfp-auth__icon--warn">
          <LinkIcon size={22} />
        </div>
        <AuthHeading
          title={expired ? "This link has expired" : "This link is incomplete"}
          lede={
            expired
              ? "It was already used or it's too old. Request a fresh one and it'll be in your inbox in a minute."
              : "The link is missing its security token. Open it straight from your email, or request a new one."
          }
        />
        <Link to="/forgot" className={buttonClass({ variant: "primary", size: "lg", block: true })}>
          Send me a new link
        </Link>
        <p className="gfp-auth__aside">
          <Link to="/login" className="gf-link">
            Back to sign in
          </Link>
        </p>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title={pageTitle}>
        <div className="gfp-auth__icon gfp-auth__icon--ok">
          <CircleCheck size={22} />
        </div>
        <AuthHeading
          title={isInvite ? "You're in." : "Password saved."}
          lede={isInvite ? "Your account is ready. Sign in to meet the family." : "You've been signed out everywhere else. Sign in with your new password."}
        />
        <Link to="/login" className={buttonClass({ variant: "primary", size: "lg", block: true })}>
          Sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={pageTitle}>
      <AuthHeading
        title={pageTitle}
        lede={isInvite ? "Welcome to the family. Pick a password to finish setting up your account." : "Pick something you don't use anywhere else."}
      />
      <form className="gfp-auth__form" onSubmit={onSubmit} noValidate>
        <Field
          label={isInvite ? "Password" : "New password"}
          hint={`At least ${MIN_PASSWORD} characters.`}
          error={touched && password.length < MIN_PASSWORD ? `Use at least ${MIN_PASSWORD} characters.` : undefined}
        >
          <PasswordInput autoComplete="new-password" size="lg" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
        </Field>
        <Field label="Confirm password" error={(touched || mismatch) && confirm !== password ? "Passwords don't match." : undefined}>
          <PasswordInput autoComplete="new-password" size="lg" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        {error ? <Callout tone="danger">{error}</Callout> : null}
        <Button type="submit" variant="primary" size="lg" block loading={busy}>
          {isInvite ? "Set password" : "Save new password"}
        </Button>
      </form>
    </AuthLayout>
  );
}

/** Old /reset?token= links → /set-password, keeping the token (and treating ?invite as mode=invite). */
export function ResetAlias() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (!params.has("mode")) params.set("mode", params.has("invite") ? "invite" : "reset");
  params.delete("invite");
  return <Navigate to={`/set-password?${params.toString()}`} replace />;
}
